import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useSpring, useInView, animate } from "framer-motion";

export const Reveal = ({ children, delay = 0, className = "", y = 24 }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
  >
    {children}
  </motion.div>
);

export const Overline = ({ children, testId, light }) => (
  <span
    data-testid={testId}
    className={`inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.2em] ${light ? "text-gold" : "text-gold"}`}
  >
    <span className="h-px w-8 bg-gold/60" />
    {children}
  </span>
);

export const SectionHeading = ({ overline, title, subtitle, testId, center }) => (
  <div className={`max-w-3xl ${center ? "mx-auto text-center" : ""}`}>
    {overline && (
      <div className={center ? "flex justify-center" : ""}>
        <Overline testId={testId ? `${testId}-overline` : undefined}>{overline}</Overline>
      </div>
    )}
    <h2 className="mt-4 font-display text-3xl md:text-5xl font-semibold tracking-tight text-navy leading-[1.1]">
      {title}
    </h2>
    {subtitle && <p className="mt-4 text-base md:text-lg text-slate-600 leading-relaxed">{subtitle}</p>}
  </div>
);

export const ScrollProgress = () => {
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 120, damping: 30, restDelta: 0.001 });
  return (
    <motion.div
      data-testid="scroll-progress"
      style={{ scaleX }}
      className="fixed top-0 left-0 right-0 h-1 origin-left z-[60] bg-gradient-to-r from-navy via-gold to-navy"
    />
  );
};

export const AnimatedCounter = ({ to, suffix = "", duration = 2, className = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const [val, setVal] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, to, {
      duration,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (v) => setVal(Math.round(v)),
    });
    return () => controls.stop();
  }, [inView, to, duration]);

  return (
    <span ref={ref} className={className}>
      {val}{suffix}
    </span>
  );
};
